import React from 'react';
import DakshinaPricingControl from '../../components/pricing/DakshinaPricingControl';
import {
  BOOKING_STEPS,
  SERVICE_GUARANTEES
} from '../../constants/bookingConstants';
import { formatPrice } from '../../utils/bookingUtils';

export const BookingBreadcrumb = ({ breadcrumb, onNavigate }) => (
  <div className="bg-white border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        {breadcrumb.map((item, index) => (
          <React.Fragment key={index}>
            {item.page ? (
              <button
                onClick={() => onNavigate && onNavigate(item.page)}
                className="hover:text-rose-600 transition-colors"
              >
                {item.label}
              </button>
            ) : (
              <span className="text-gray-900 font-medium">{item.label}</span>
            )}
            {index < breadcrumb.length - 1 && <span>•</span>}
          </React.Fragment>
        ))}
      </nav>
    </div>
  </div>
);

export const BookingHeader = ({ currentStep, steps = BOOKING_STEPS }) => (
  <div className="bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Booking</h1>
        <p className="text-gray-600">Fill in the details below to confirm your service booking</p>
      </div>

      {steps.length <= 1 ? (
        <div className="max-w-md mx-auto mb-8 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-800 text-center">
          One-step booking form. Fill details once and confirm instantly.
        </div>
      ) : (
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    currentStep >= step.number
                      ? 'bg-rose-600 border-rose-600 text-white'
                      : 'border-gray-300 text-gray-500'
                  }`}>
                    {currentStep > step.number ? '✓' : step.icon}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <div className={`text-sm font-medium ${
                      currentStep >= step.number ? 'text-rose-600' : 'text-gray-500'
                    }`}>
                      Step {step.number}
                    </div>
                    <div className={`text-xs ${
                      currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-0.5 ${
                    currentStep > step.number ? 'bg-rose-600' : 'bg-gray-300'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

export const BookingFormNavigation = ({
  submitState,
  currentStep = 1,
  prevStep = () => {},
  nextStep = () => {},
  totalSteps = 3
}) => {
  if (totalSteps <= 1) {
    return (
      <div className="flex justify-end pt-6 border-t border-gray-200 mt-8">
        <div className="flex flex-col items-end">
          {submitState.error && (
            <p className="text-sm text-red-600 mb-2">{submitState.error}</p>
          )}
          <button
            type="submit"
            disabled={submitState.loading}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              submitState.loading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-rose-600 text-white hover:bg-rose-700'
            }`}
          >
            {submitState.loading ? 'Submitting...' : 'Confirm Booking'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between pt-6 border-t border-gray-200 mt-8">
      <button
        type="button"
        onClick={prevStep}
        disabled={currentStep === 1}
        className={`px-6 py-2 border border-gray-300 rounded-lg font-medium transition-colors ${
          currentStep === 1
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        Previous
      </button>

      {currentStep < totalSteps ? (
        <button
          type="button"
          onClick={nextStep}
          className="px-6 py-2 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 transition-colors"
        >
          Next Step
        </button>
      ) : (
        <div className="flex flex-col items-end">
          {submitState.error && (
            <p className="text-sm text-red-600 mb-2">{submitState.error}</p>
          )}
          <button
            type="submit"
            disabled={submitState.loading}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              submitState.loading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-rose-600 text-white hover:bg-rose-700'
            }`}
          >
            {submitState.loading ? 'Submitting...' : 'Confirm Booking'}
          </button>
        </div>
      )}
    </div>
  );
};

export const BookingOrderSummary = ({
  serviceInfo,
  packageInfo,
  addOnItems,
  totalAmount,
  pricingBreakdown,
  servicePricingConfig,
  hasDakshinaPricing,
  onDakshinaChange,
  bookingMode,
  selectedPanditName,
  formData
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-4">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>

    <div className="flex items-center space-x-3 mb-4">
      <div className="text-3xl">{serviceInfo.image}</div>
      <div>
        <div className="font-medium text-gray-900">{serviceInfo.title}</div>
        <div className="text-sm text-gray-600">{packageInfo.name}</div>
        <div className="text-xs text-indigo-700 mt-1">
          Booking mode: {bookingMode === 'pandit' ? 'Direct Pandit' : 'Service Catalog'}
        </div>
        {selectedPanditName && (
          <div className="text-xs text-indigo-700">Selected pandit: {selectedPanditName}</div>
        )}
      </div>
    </div>

    {hasDakshinaPricing && servicePricingConfig?.dakshina && (
      <DakshinaPricingControl
        min={servicePricingConfig.dakshina.min}
        max={servicePricingConfig.dakshina.max}
        step={servicePricingConfig.dakshina.step}
        value={Number(pricingBreakdown?.dakshina || servicePricingConfig.dakshina.defaultValue || 0)}
        onChange={onDakshinaChange}
        formatPrice={formatPrice}
        variant="compact"
      />
    )}

    <div className="space-y-2 mb-4">
      <div className="flex justify-between">
        <span className="text-sm text-gray-600">
          {pricingBreakdown?.baseLabel || 'Package Price'}
        </span>
        <span className="text-sm font-medium">
          {formatPrice(Number(pricingBreakdown?.dakshina || packageInfo.price || 0))}
        </span>
      </div>

      {pricingBreakdown?.schedule && (
        <div className="text-xs text-gray-500">
          {pricingBreakdown.schedule.pandits} pandit(s) x {pricingBreakdown.schedule.days} day(s) x {formatPrice(pricingBreakdown.schedule.ratePerPanditPerDay)}
        </div>
      )}

      {addOnItems && addOnItems.length > 0 && (
        <>
          {addOnItems.map((addOn, index) => (
            <div key={index} className="flex justify-between">
              <span className="text-sm text-gray-600">{addOn.name}</span>
              <span className="text-sm font-medium">+{formatPrice(addOn.price)}</span>
            </div>
          ))}
        </>
      )}

      {Number(pricingBreakdown?.platformFee || 0) > 0 && (
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Platform Fee</span>
          <span className="text-sm font-medium">{formatPrice(pricingBreakdown.platformFee)}</span>
        </div>
      )}

      {pricingBreakdown?.platformFeeNote && (
        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-2">
          {pricingBreakdown.platformFeeNote}
        </div>
      )}

      <div className="border-t border-gray-200 pt-2">
        <div className="flex justify-between">
          <span className="font-medium text-gray-900">Total Amount</span>
          <span className="font-bold text-gray-900">{formatPrice(totalAmount)}</span>
        </div>
      </div>

      <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 mt-4">
        <div className="text-sm text-rose-700 font-medium">
          No online payment required right now
        </div>
        <div className="text-xs text-rose-600 mt-1">
          Our team will confirm the booking and share payment details separately.
        </div>
      </div>
    </div>

    <div className="border-t border-gray-200 pt-4">
      <h4 className="font-medium text-gray-900 mb-3">Service Guarantees</h4>
      <div className="space-y-2">
        {SERVICE_GUARANTEES.map((guarantee, index) => (
          <div key={index} className="flex items-center space-x-2">
            <span className="text-sm">{guarantee.icon}</span>
            <span className="text-xs text-gray-600">{guarantee.text}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="border-t border-gray-200 pt-4 mt-4">
      <div className="text-center">
        <div className="text-sm text-gray-600 mb-2">Need help?</div>
        <button
          type="button"
          onClick={() => console.log('Contact support')}
          className="text-rose-600 hover:text-rose-700 text-sm font-medium"
        >
          📞 Contact Support
        </button>
      </div>
    </div>
  </div>
);
